import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Logical (CSS) drawing size of the inline pad. The backing store is scaled by
 * devicePixelRatio for crispness; export is at least 600x300 device px because
 * EXPORT_W/EXPORT_H are themselves 600x300 logical and re-scaled on export.
 */
const EXPORT_W = 600;
const EXPORT_H = 300;
const LINE_WIDTH = 2.5;

interface Point {
  x: number;
  y: number;
}

interface SignaturePadProps {
  /** Submit a white-background PNG data URL. */
  onSubmit: (dataUrl: string) => void;
  submitting: boolean;
  disabled?: boolean;
}

export default function SignaturePad({ onSubmit, submitting, disabled }: SignaturePadProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [isEmpty, setIsEmpty] = useState(true);
  const [fullscreen, setFullscreen] = useState(false);

  // Stroke state lives in refs so pointermove never triggers React re-renders.
  const drawingRef = useRef(false);
  const pointsRef = useRef<Point[]>([]);
  const dprRef = useRef(1);

  /**
   * Size the canvas backing store to its CSS box * devicePixelRatio, then map
   * the context so we can draw in CSS pixels. NOTE (MVP): resizing clears the
   * canvas, so entering/exiting fullscreen starts a fresh signature. This is
   * intentional and kept predictable rather than re-projecting strokes.
   */
  const setupCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    dprRef.current = dpr;
    canvas.width = Math.max(1, Math.round(rect.width * dpr));
    canvas.height = Math.max(1, Math.round(rect.height * dpr));
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.lineWidth = LINE_WIDTH;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#000000';
    ctx.clearRect(0, 0, rect.width, rect.height);
    pointsRef.current = [];
    setIsEmpty(true);
  }, []);

  // (Re)setup whenever we toggle fullscreen, and on window resize.
  useEffect(() => {
    setupCanvas();
    const onResize = () => setupCanvas();
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
    };
  }, [setupCanvas, fullscreen]);

  const pointFromEvent = (e: React.PointerEvent<HTMLCanvasElement>): Point => {
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  /**
   * Classic signature smoothing: draw a quadratic bézier whose control point is
   * the latest raw point and whose endpoint is the midpoint between the last two
   * raw points. This yields smooth strokes without buffering the whole path.
   */
  const drawSmooth = (ctx: CanvasRenderingContext2D) => {
    const pts = pointsRef.current;
    const n = pts.length;
    if (n < 3) return;
    const c = pts[n - 2];
    const prev = pts[n - 3];
    const start: Point = { x: (prev.x + c.x) / 2, y: (prev.y + c.y) / 2 };
    const end: Point = { x: (c.x + pts[n - 1].x) / 2, y: (c.y + pts[n - 1].y) / 2 };
    ctx.beginPath();
    ctx.moveTo(start.x, start.y);
    ctx.quadraticCurveTo(c.x, c.y, end.x, end.y);
    ctx.stroke();
  };

  const handlePointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (disabled || submitting) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.setPointerCapture(e.pointerId);
    drawingRef.current = true;
    const p = pointFromEvent(e);
    pointsRef.current = [p];
    // Dot for a single tap.
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.beginPath();
      ctx.arc(p.x, p.y, LINE_WIDTH / 2, 0, Math.PI * 2);
      ctx.fillStyle = '#000000';
      ctx.fill();
    }
    if (isEmpty) setIsEmpty(false);
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    pointsRef.current.push(pointFromEvent(e));
    drawSmooth(ctx);
  };

  const endStroke = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    const canvas = canvasRef.current;
    if (canvas && canvas.hasPointerCapture(e.pointerId)) {
      canvas.releasePointerCapture(e.pointerId);
    }
    pointsRef.current = [];
  };

  const handleClear = () => {
    setupCanvas();
  };

  /**
   * Export at a fixed 600x300 logical resolution scaled by DPR (>= 600x300
   * device px), compositing the strokes over a white background. We re-render
   * from the live canvas by scaling its current pixels onto a clean export
   * canvas with a white fill underneath.
   */
  const handleSubmit = () => {
    if (isEmpty || submitting || disabled) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    const out = document.createElement('canvas');
    out.width = Math.round(EXPORT_W * dpr);
    out.height = Math.round(EXPORT_H * dpr);
    const octx = out.getContext('2d');
    if (!octx) return;
    // White background first, then the transparent-stroke source canvas on top.
    octx.fillStyle = '#ffffff';
    octx.fillRect(0, 0, out.width, out.height);
    octx.drawImage(canvas, 0, 0, canvas.width, canvas.height, 0, 0, out.width, out.height);
    onSubmit(out.toDataURL('image/png'));
  };

  const canvasEl = (
    <canvas
      ref={canvasRef}
      className={fullscreen ? 'sig-canvas sig-canvas--full' : 'sig-canvas'}
      style={{ touchAction: 'none' }}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={endStroke}
      onPointerCancel={endStroke}
      onPointerLeave={endStroke}
    />
  );

  if (fullscreen) {
    return (
      <div className="sig-fullscreen">
        <div className="sig-fullscreen__bar">
          <span className="sig-fullscreen__hint">请横屏在下方区域内签字</span>
          <button type="button" className="btn btn--primary" onClick={() => setFullscreen(false)}>
            完成
          </button>
        </div>
        {canvasEl}
        <div className="sig-fullscreen__actions">
          <button type="button" className="btn" onClick={handleClear}>
            清除重写
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="sig">
      <div className="sig__head">
        <span className="sig__hint">请在下方灰色区域内签字</span>
        <button
          type="button"
          className="btn btn--ghost sig__fsbtn"
          onClick={() => setFullscreen(true)}
        >
          全屏签字
        </button>
      </div>
      <div className="sig__area">{canvasEl}</div>
      <div className="sig__actions">
        <button type="button" className="btn" onClick={handleClear} disabled={submitting}>
          清除重写
        </button>
        <button
          type="button"
          className="btn btn--primary"
          onClick={handleSubmit}
          disabled={isEmpty || submitting || disabled}
        >
          {submitting ? '提交中…' : '签好了，提交'}
        </button>
      </div>
    </div>
  );
}
